import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, Camera, Coins, PlusCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { VoiceDictateButton } from '@/components/ui/voice-dictate-button';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';

const COMPLEXITY_KEYWORDS = [
  'analyser',
  'analyse',
  'comparer',
  'comparaison',
  'prévoir',
  'prévision',
  'forecast',
  'prédire',
  'créer',
  'ajouter',
  'modifier',
  'supprimer',
  'inventaire',
  'document',
  'rapport',
  'pdf',
  'excel',
  'word',
  'vendre',
  'vente',
  'facture',
  'segmentation',
  'anomalie',
  'tendance',
  'performance',
  'santé',
  'health',
  'reorder',
  'réapprovisionner',
  'générer',
  'exporter',
];

function estimateCredits(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;
  const lower = trimmed.toLowerCase();
  const isComplex = trimmed.length > 200 || COMPLEXITY_KEYWORDS.some((kw) => lower.includes(kw));
  return isComplex ? '~2-3 crédits' : '~1 crédit';
}

export interface ChatQuickCommand {
  command: string;
  label: string;
  prompt: string;
}

export type MobileInputVariant = 'default' | 'whatsapp' | 'imessage' | 'minimal';

interface ChatInputProps {
  onSend: (message: string, image?: File) => void;
  disabled?: boolean;
  placeholder?: string;
  draftKey?: string;
  maxLength?: number;
  quickCommands?: ChatQuickCommand[];
  mobileCompact?: boolean;
  enableImageUpload?: boolean;
  mobileVariant?: MobileInputVariant;
}

function storageKey(draftKey: string) {
  return `chat:draft:${draftKey}`;
}

function mapCommandMessage(rawValue: string, quickCommands: ChatQuickCommand[]): string {
  const trimmed = rawValue.trim();
  if (!trimmed.startsWith('/')) return trimmed;

  const [rawCommand = '', ...rest] = trimmed.split(/\s+/);
  const command = rawCommand.slice(1).toLowerCase();
  const match = quickCommands.find((item) => item.command.toLowerCase() === command);
  if (!match) return trimmed;

  if (rest.length === 0) return match.prompt;
  return `${match.prompt} ${rest.join(' ')}`.trim();
}

export function ChatInput({
  onSend,
  disabled = false,
  placeholder,
  draftKey,
  maxLength = 8000,
  quickCommands = [],
  mobileCompact = false,
  enableImageUpload = false,
  mobileVariant = 'default',
}: ChatInputProps) {
  const { t } = useTranslation('dashboard');
  const isMobile = useIsMobile();
  const [value, setValue] = useState('');
  const [activeCommandIndex, setActiveCommandIndex] = useState(0);
  const [focused, setFocused] = useState(false);
  const [attachedImage, setAttachedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const quickMenuId = `chat-quick-menu-${draftKey ?? 'default'}`;

  useEffect(() => {
    if (!draftKey) return;

    try {
      const saved = localStorage.getItem(storageKey(draftKey));
      if (typeof saved === 'string') {
        setValue(saved);
      }
    } catch {
      // Ignore localStorage failures.
    }
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey) return;
    try {
      localStorage.setItem(storageKey(draftKey), value);
    } catch {
      // Ignore localStorage failures.
    }
  }, [draftKey, value]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  }, [value]);

  const availableCommands = useMemo(() => {
    const trimmed = value.trim();
    if (!trimmed.startsWith('/')) return [];

    const search = trimmed.slice(1).toLowerCase();
    return quickCommands.filter(
      (item) =>
        item.command.toLowerCase().includes(search) || item.label.toLowerCase().includes(search),
    );
  }, [quickCommands, value]);

  const characters = value.length;
  const isOverLimit = characters > maxLength;
  const compactMobileMode = mobileCompact && isMobile;
  const creditEstimate = estimateCredits(value);

  function handleSend() {
    const parsed = mapCommandMessage(value, quickCommands);
    if (!parsed || disabled || isOverLimit) return;

    onSend(parsed, attachedImage ?? undefined);
    setValue('');
    setActiveCommandIndex(0);
    setAttachedImage(null);
    setImagePreview(null);

    if (draftKey) {
      try {
        localStorage.removeItem(storageKey(draftKey));
      } catch {
        // Ignore localStorage failures.
      }
    }

    textareaRef.current?.focus();
  }

  function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachedImage(file);
    const url = URL.createObjectURL(file);
    setImagePreview(url);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  }

  function removeImage() {
    setAttachedImage(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  }

  function applyCommand(command: ChatQuickCommand) {
    setValue(command.prompt);
    setActiveCommandIndex(0);
    textareaRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (availableCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveCommandIndex((prev) => (prev + 1) % availableCommands.length);
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveCommandIndex((prev) => (prev === 0 ? availableCommands.length - 1 : prev - 1));
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        const selected = availableCommands[activeCommandIndex];
        if (selected) {
          e.preventDefault();
          applyCommand(selected);
          return;
        }
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // Mode mobile redesigné (immersif) : rendu distinct
  if (compactMobileMode) {
    const hasText = value.trim().length > 0;
    const showCharCount = characters > maxLength * 0.8;

    return (
      <div
        className="bg-[#f5f7f9] dark:bg-charcoal-900 px-4 py-2 border-0"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        {availableCommands.length > 0 && (
          <div
            id={quickMenuId}
            className="mb-2 rounded-xl border-0 bg-white dark:bg-charcoal-800 shadow-lg overflow-hidden"
            role="listbox"
            aria-label={t('chat_quick_commands')}
          >
            {availableCommands.slice(0, 4).map((item, index) => (
              <button
                key={item.command}
                type="button"
                onClick={() => applyCommand(item)}
                className={cn(
                  'w-full px-3 py-2.5 text-left text-sm border-b border-slate-100 dark:border-charcoal-700 last:border-b-0',
                  index === activeCommandIndex ? 'bg-muted/80 text-charcoal-900' : 'bg-card',
                )}
              >
                <span className="font-mono text-xs text-charcoal-400">/{item.command}</span>
                <span className="ml-2 font-medium text-charcoal-800 dark:text-charcoal-200">
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        )}

        {imagePreview && (
          <div className="mb-2 relative inline-block">
            <img
              src={imagePreview}
              alt="Pièce jointe"
              className="h-16 rounded-lg border border-charcoal-200 dark:border-charcoal-700 object-cover"
            />
            <button
              type="button"
              onClick={removeImage}
              className="absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-charcoal-900 text-white text-[10px]"
              aria-label="Supprimer l'image"
            >
              <X className="size-3" />
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          onChange={handleImageSelect}
          className="hidden"
        />

        <div
          className={cn(
            'flex items-center gap-2 rounded-full bg-white dark:bg-charcoal-800 shadow-[0_4px_20px_rgba(0,0,0,0.04)] border-0 px-3 py-1.5 focus-within:shadow-[0_4px_25px_rgba(118,185,0,0.15)] transition-all',
          )}
        >
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex size-7 items-center justify-center rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0 transition-colors"
            aria-label="Ajouter une pièce jointe"
          >
            <PlusCircle className="size-5" />
          </button>

          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder ?? 'Posez une question à Velko...'}
            rows={1}
            maxLength={maxLength}
            aria-describedby={`${quickMenuId}-counter`}
            aria-expanded={availableCommands.length > 0}
            aria-controls={quickMenuId}
            className="flex-1 resize-none border-0 bg-transparent p-0 py-1 text-sm text-slate-800 dark:text-white placeholder:text-slate-400 shadow-none outline-none ring-0 focus-visible:ring-0 min-h-[28px] max-h-[160px] leading-relaxed"
          />

          <div className="flex shrink-0 items-center gap-1.5 self-center">
            {enableImageUpload && !hasText && !attachedImage && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex size-8 items-center justify-center rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                aria-label="Joindre une photo"
              >
                <Camera className="size-4" />
              </button>
            )}
            <VoiceDictateButton
              onTranscript={(text) => setValue((prev) => (prev ? `${prev} ${text}` : text))}
              size="icon"
              className="size-8 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            />
            <motion.button
              type="button"
              onClick={handleSend}
              disabled={disabled || isOverLimit}
              aria-label={t('chat_send')}
              whileTap={{ scale: 0.92 }}
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-full bg-[#76b900] text-white shadow-md shadow-[#76b900]/30 hover:bg-[#68a400] active:scale-95 transition-all',
                (disabled || isOverLimit) && 'opacity-50 cursor-not-allowed',
              )}
            >
              <ArrowUp className="size-4 stroke-[2.5]" />
            </motion.button>
          </div>
        </div>

        {mobileVariant !== 'minimal' && (
          <div className="mt-1 flex items-center justify-between px-1">
            {creditEstimate ? (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-500">
                <Coins className="size-2.5" />
                {creditEstimate}
              </span>
            ) : (
              <span />
            )}
            {showCharCount && (
              <p
                id={`${quickMenuId}-counter`}
                className={cn(
                  'text-[10px]',
                  isOverLimit ? 'text-red-500 font-semibold' : 'text-charcoal-400',
                )}
              >
                {characters}/{maxLength}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Mode desktop : redesigné
  return (
    <div className="bg-background px-3 py-2">
      {imagePreview && (
        <div className="mb-2 relative inline-block">
          <img
            src={imagePreview}
            alt="Pièce jointe"
            className="h-20 rounded-lg border border-charcoal-200 dark:border-charcoal-700 object-cover"
          />
          <button
            type="button"
            onClick={removeImage}
            className="absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-charcoal-900 text-white"
            aria-label="Supprimer l'image"
          >
            <X className="size-3" />
          </button>
        </div>
      )}

      <input
        ref={!compactMobileMode ? fileInputRef : undefined}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleImageSelect}
        className="hidden"
      />

      <div className="relative flex items-center gap-2 rounded-full bg-white dark:bg-charcoal-800 shadow-[0_4px_20px_rgba(0,0,0,0.04)] border-0 px-3 py-2 focus-within:shadow-[0_4px_25px_rgba(118,185,0,0.15)] transition-all">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex size-7 items-center justify-center rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0 transition-colors"
          aria-label="Ajouter une pièce jointe"
        >
          <PlusCircle className="size-5" />
        </button>

        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder ?? 'Posez une question à Velko...'}
          rows={1}
          maxLength={maxLength}
          aria-describedby={`${quickMenuId}-counter`}
          aria-expanded={availableCommands.length > 0}
          aria-controls={quickMenuId}
          className="flex-1 resize-none border-0 bg-transparent p-0 px-1 py-1 text-sm text-slate-800 dark:text-white placeholder:text-slate-400 shadow-none outline-none ring-0 focus-visible:ring-0 min-h-[32px] max-h-[160px] leading-relaxed"
        />

        <div className="flex shrink-0 items-center gap-1.5 self-center">
          {enableImageUpload && !value.trim() && !attachedImage && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex size-8 items-center justify-center rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
              aria-label="Joindre une photo"
            >
              <Camera className="size-4" />
            </button>
          )}
          <VoiceDictateButton
            onTranscript={(text) => setValue((prev) => (prev ? `${prev} ${text}` : text))}
            size="icon"
            className="size-8 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={disabled || isOverLimit}
            aria-label={t('chat_send')}
            className="size-9 rounded-full bg-[#76b900] text-white shadow-md shadow-[#76b900]/30 hover:bg-[#68a400] active:scale-95 transition-all"
          >
            <ArrowUp className="size-4 stroke-[2.5]" />
          </Button>
        </div>

        {availableCommands.length > 0 && (
          <div
            id={quickMenuId}
            className="absolute bottom-full left-0 right-0 mb-2 rounded-xl border border-charcoal-200 dark:border-charcoal-700 bg-card shadow-lg overflow-hidden"
            role="listbox"
            aria-label={t('chat_quick_commands')}
          >
            {availableCommands.slice(0, 4).map((item, index) => (
              <button
                key={item.command}
                type="button"
                onClick={() => applyCommand(item)}
                className={cn(
                  'w-full px-3 py-2.5 text-left text-sm border-b border-charcoal-100 dark:border-charcoal-700 last:border-b-0',
                  index === activeCommandIndex ? 'bg-muted/80 text-charcoal-900' : 'bg-card',
                )}
              >
                <span className="font-mono text-xs text-charcoal-400">/{item.command}</span>
                <span className="ml-2 font-medium text-charcoal-800 dark:text-charcoal-200">
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between px-1 pt-1">
        <p
          className={cn(
            'text-[11px] text-muted-foreground transition-opacity',
            focused ? 'opacity-100' : 'opacity-0',
          )}
        >
          {focused ? t('chat_input_hint') : ''}
          {focused && creditEstimate && (
            <span className="ml-2 inline-flex items-center gap-0.5 text-amber-500">
              <Coins className="inline size-2.5" />
              {creditEstimate}
            </span>
          )}
        </p>
        {characters > maxLength * 0.8 && (
          <p
            id={`${quickMenuId}-counter`}
            className={cn(
              'text-[11px]',
              isOverLimit ? 'text-error font-semibold' : 'text-muted-foreground',
            )}
          >
            {characters}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
}
